import { Stack, Typography } from '@mui/material';

import { Markdown } from './Markdown';
import { VariableMatchSummaryRow } from './VariableMatchSummaryRow';
import type { AvSelectionCandidate } from '../types/epi-chat-types';
import type { ReviewSummaryEntry } from '../types/review-types';

interface ReviewSummaryRowProps {
  entry: ReviewSummaryEntry;
  /** Label for a match left on "build new" (the variable-match step's own wording). */
  buildNewLabel: string;
}

/** One item's outcome on the review and resolved screens. */
export const ReviewSummaryRow = ({ entry, buildNewLabel }: ReviewSummaryRowProps) => {
  const { item, pick } = entry;

  if (item.kind === 'match') {
    const reused: AvSelectionCandidate | null = pick?.type === 'use' ? pick.candidate : null;
    return (
      <VariableMatchSummaryRow
        item={{
          checkpointId: item.id,
          requestedLabel: item.checkpoint.requestedVariable ?? item.checkpoint.summary,
          selectionLabel: reused ? reused.dataElement : buildNewLabel,
          reusedCandidate: reused,
        }}
      />
    );
  }

  const answer = pick?.type === 'option' ? pick.label : pick?.type === 'custom' ? pick.text : '';
  return (
    <Stack
      spacing={1}
      data-testid={`review-summary-${item.id}`}
      sx={{
        paddingY: 3,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {/* The agent writes questions in Markdown (e.g. **bold**), as the question step renders them. */}
      <Markdown dense>{item.question.question}</Markdown>
      {answer && (
        <Typography variant="body2" color="text.secondary">
          {answer}
        </Typography>
      )}
    </Stack>
  );
};
